import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { Box, Typography } from '@mui/material'
import { TableColumn, TAttribute, TProduct, TProductWithId } from 'types'
import { DeleteIcon, EditIcon, Table } from 'components'
import { CrudAction, Entity } from 'utils/hooks'
import { successToast } from 'utils/toastService'
import { EmptyState } from 'components/ui/EmptyState/EmptyState'
import useDeleteModal from '../dialogs/useDeleteModal'
import useEditModal from '../dialogs/useEditModal'
import { TableCard } from './Products.styled'
import * as sxStyles from './Products.styled'
import { ProductsModuleAdapter } from '../types/shared.types'
import { ListPagination } from '../../../../../services/types'
import { TableActionConfig } from '../../../../../components/Table/types'
import { TableActions } from '../../../../../components/Table/TableActions'

type TProductsTableColumnsProps = {
  adapter: ProductsModuleAdapter
  products: TProductWithId[]
  pagination: ListPagination
  isProductsLoading: boolean
  attributes: TAttribute[]
  setBlendingSpecCurrentPage: (page: number) => void
  setBlendingSpecRowsPerPage: (rows: number) => void
  tableKey?: string
}

const formatAttrToRange = (row: TProduct, attributeName: string) => {
  const formattedData = row.attributes?.find(
    (currentRow) => currentRow.name === attributeName
  )
  return (
    <div>
      {formattedData?.range_min} - {formattedData?.range_max}
    </div>
  )
}

export const productsTableColumnsActions = <T extends TProductWithId>(
  actions: TableActionConfig<T>[],
  attributes: TAttribute[] = [],
  nameLabel: string = 'Name'
): TableColumn[] => [
  {
    id: 'name',
    label: nameLabel,
    width: 200,
    align: 'center',
    isStickyLeft: true,
  },
  ...attributes?.map((attr: TAttribute) => ({
    id: attr.name,
    label: attr.name,
    align: 'left' as const,
    format: (_: unknown, row: TProduct) => formatAttrToRange(row, attr.name),
  })),
  {
    id: 'actions',
    label: '',
    align: 'left',
    format: (_, row: T) => <TableActions actions={actions} row={row} />,
    isStickyRight: true,
  },
]

export const ProductsTableColumns = ({
  adapter,
  products,
  pagination,
  isProductsLoading,
  attributes,
  setBlendingSpecCurrentPage,
  setBlendingSpecRowsPerPage,
  tableKey = 'blendingSpec',
}: TProductsTableColumnsProps) => {
  const deleteFn = adapter.useDelete()
  const updateFn = adapter.useUpdate()

  const queryClient = useQueryClient()
  const [deleteItemId, setDeleteItemId] = useState<number | null>(null)
  const [editingItem, setEditingItem] = useState<TProduct | null>(null)

  const { mutate: deleteProductMutation } = useMutation(deleteFn, {
    onSuccess: (data) => {
      setDeleteItemId(null)
      void queryClient.invalidateQueries([`${adapter.invalidatQueryName}`])
      successToast(data?.detail ? data.detail : adapter.toasts.deleted)
    },
    onError: (error: { detail?: string }) => {
      toast.error(error?.detail ? error.detail : adapter.toasts.deleteFailed)
    },
  })

  const { mutateAsync: updateProductMutation } = useMutation(updateFn, {
    onSuccess: () => {
      void queryClient.invalidateQueries([`${adapter.invalidatQueryName}`])
      successToast(adapter.toasts.updated)
      toggleEditModal()
      resetEditModal()
    },
    onError: (error: { detail?: string }) => {
      toast.error(error?.detail ? error.detail : adapter.toasts.updateFailed)
    },
  })

  const [deleteModal, toggleDeleteModal] = useDeleteModal({
    children: (
      <Typography sx={sxStyles.deleteModalTypography}>
        {adapter.labels?.deleteConfirmTitle ??
          'Are you sure you want to remove this item?'}
      </Typography>
    ),
    onConfirm: () => {
      if (deleteItemId !== null) {
        void deleteProductMutation(deleteItemId)
      }
    },
  })

  const { data: categoriesData } = adapter.useCategories
    ? adapter.useCategories()
    : { data: undefined }

  const memoizedCategoriesResults = useMemo(() => {
    return categoriesData?.results
  }, [categoriesData?.results])

  const validationRules = useMemo(() => {
    return adapter.computeValidationRules
      ? adapter.computeValidationRules(attributes)
      : undefined
  }, [adapter.computeValidationRules, attributes])

  const [editModal, toggleEditModal, resetEditModal] = useEditModal({
    initialData: editingItem,
    mode: 'edit',
    titleEdit: `Edit ${adapter?.headerTitle}`,
    titleAdd: `Add ${adapter?.headerTitle}`,
    validationRules,
    categoriesWithAttributes: categoriesData?.results,
    onConfirm: async (data: TProduct) => {
      try {
        await updateProductMutation(data)
        setEditingItem(null)
      } catch (error) {
        console.error('Failed to update product:', error)
      }
    },
  })

  const actions: TableActionConfig<TProduct>[] = [
    {
      name: 'edit',
      icon: <EditIcon size={16} />,
      handler: (row: TProduct) => {
        setEditingItem(row)
        toggleEditModal()
      },
    },
    {
      name: 'delete',
      icon: <DeleteIcon size={20} />,
      handler: (row: TProduct) => {
        if (!row?.id) return
        setDeleteItemId(row.id)
        toggleDeleteModal()
      },
      permission: {
        entity: adapter.permissionEntity ?? Entity.PRODUCT,
        action: CrudAction.DELETE,
      },
    },
  ]

  const updatedColumns = useMemo(
    () =>
      productsTableColumnsActions(
        actions,
        attributes,
        adapter.labels?.nameColumn ?? 'Name'
      ),
    [attributes, adapter.labels?.nameColumn]
  )

  const onBlendSpecPaginationChange = (page: number, perPage: number) => {
    setBlendingSpecCurrentPage(page)
    setBlendingSpecRowsPerPage(perPage)
  }

  return (
    <TableCard>
      <Box sx={sxStyles.boxTableWrapper}>
        {products?.length > 0 ? (
          <Table
            columns={updatedColumns}
            data={products}
            showPagination={true}
            isScrollX={true}
            onPaginationChange={onBlendSpecPaginationChange}
            total={pagination?.total || 0}
            tableKey={tableKey}
          />
        ) : isProductsLoading ? (
          <Typography>{adapter.labels?.loadingText ?? 'Loading...'}</Typography>
        ) : (
          <EmptyState />
        )}
      </Box>
      {deleteModal}
      {editModal}
    </TableCard>
  )
}
